import { useState, useEffect, useCallback } from "react";
import { useApiRequest } from "../../../../hooks/useApiRequest";
import { settings } from "../../../../shared/settings";
import {
  Box,
  VStack,
  HStack,
  Text,
  IconButton,
  Flex,
  Badge,
  Button,
  Icon,
  useColorModeValue,
  Input,
  FormControl,
  FormLabel,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Switch,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormErrorMessage,
  SimpleGrid,
  Grid,
  Select,
  Tooltip,
  Divider,
  NumberInput,
  NumberInputField,
  Collapse,
} from "@chakra-ui/react";
import { RichTextEditor } from "../../../../components/RichTextEditor";
import { DragDropContext, Droppable, Draggable, type DropResult } from "@hello-pangea/dnd";
import { GripVertical, Pencil, Trash2, Plus, X, Sparkles, Info, ChevronDown, Link2 } from "lucide-react";
import type { FormBuilderField } from "./useFormBuilder";
import { VisibilityRulesBuilder } from "./VisibilityRulesBuilder";
import { FieldKeySelector } from "./FieldKeySelector";
import type { AvailableKey } from "./FieldKeySelector";
import {
  FIELD_TYPE_LABELS,
  STANDARD_FIELD_TYPES,
  GUPY_FIELD_TYPES,
  type FieldType,
  type FieldOption,
  type ValidationRules,
} from "../types";

const NEEDS_OPTIONS: FieldType[] = ["select", "multi_select"];

const GUPY_GROUPS: { label: string; types: FieldType[] }[] = [
  { label: "Dados pessoais", types: ["full_name", "email", "cpf", "phone", "birth_date"] },
  { label: "Endereço",       types: ["cep", "address_street", "address_number", "address_city", "address_state"] },
  { label: "Vaga",           types: ["job_title", "job_department"] },
  { label: "Outros",         types: ["pcd"] },
];

interface FieldFormState {
  key: string;
  label: string;
  details: string;
  field_type: FieldType;
  is_required: boolean;
  is_pj: boolean;
  is_clt: boolean;
  options: FieldOption[];
  validation_rules: ValidationRules | null;
  visibility_rules: any;
  reference_source: { field_key: string } | null;
}

const DEFAULT_FIELD: FieldFormState = {
  key: "",
  label: "",
  details: "",
  field_type: "string",
  is_required: false,
  is_pj: true,
  is_clt: true,
  options: [],
  validation_rules: null,
  visibility_rules: null,
  reference_source: null,
};

interface FieldTypeSelectProps {
  value: FieldType;
  inputBg: string;
  onChange: (v: FieldType) => void;
}

function FieldTypeSelect({ value, inputBg, onChange }: FieldTypeSelectProps) {
  const isGupy     = GUPY_FIELD_TYPES.includes(value);
  const menuBg     = useColorModeValue("white", "#0f2040");
  const hoverBg    = useColorModeValue("gray.50", "whiteAlpha.100");
  const selectedBg = useColorModeValue("blue.50", "rgba(0,158,251,0.08)");
  const gupySelBg  = useColorModeValue("purple.50", "rgba(128,90,213,0.1)");
  const divColor   = useColorModeValue("gray.100", "whiteAlpha.100");

  return (
    <Menu matchWidth isLazy>
      <MenuButton
        as={Button}
        w="full"
        h="40px"
        px={3}
        variant="outline"
        bg={inputBg}
        fontSize="sm"
        fontWeight={400}
        textAlign="left"
        rightIcon={<ChevronDown size={14} />}
        _hover={{ bg: inputBg }}
        _active={{ bg: inputBg }}
        sx={{ "& > span:first-of-type": { flex: 1 } }}
      >
        <HStack spacing={1.5}>
          {isGupy && (
            <Icon as={Sparkles} boxSize={3} color="purple.500" flexShrink={0} />
          )}
          <Text
            fontSize="sm"
            fontWeight={400}
            color={isGupy ? "purple.600" : "inherit"}
            _dark={{ color: isGupy ? "purple.300" : "inherit" }}
            noOfLines={1}
          >
            {FIELD_TYPE_LABELS[value]}
          </Text>
        </HStack>
      </MenuButton>

      <MenuList
        bg={menuBg}
        py={2}
        maxH="320px"
        overflowY="auto"
        shadow="lg"
        borderRadius="xl"
        zIndex={1600}
        minW={0}
      >
        {/* ── Tipos padrão ── */}
        <Box px={3} pb={1}>
          <Text
            fontSize="9px"
            fontWeight={700}
            color="gray.400"
            textTransform="uppercase"
            letterSpacing="0.1em"
          >
            Tipos padrão
          </Text>
        </Box>
        {STANDARD_FIELD_TYPES.map((v) => (
          <MenuItem
            key={v}
            onClick={() => onChange(v)}
            pl={4}
            py={1.5}
            fontSize="sm"
            bg={value === v ? selectedBg : "transparent"}
            fontWeight={value === v ? 600 : 400}
            _hover={{ bg: value === v ? selectedBg : hoverBg }}
          >
            {FIELD_TYPE_LABELS[v]}
          </MenuItem>
        ))}

        {/* ── Divisor ── */}
        <Box my={2} borderTopWidth="1px" borderColor={divColor} />

        {/* ── Grupos Gupy ── */}
        {GUPY_GROUPS.flatMap((group, gIdx) => [
          ...(gIdx > 0
            ? [
                <Box
                  key={`sep-${group.label}`}
                  my={1.5}
                  mx={3}
                  borderTopWidth="1px"
                  borderColor={divColor}
                />,
              ]
            : []),
          <Box
            key={`hdr-${group.label}`}
            px={3}
            py={1}
            display="flex"
            alignItems="center"
            gap="6px"
          >
            <Icon as={Sparkles} boxSize="10px" color="purple.400" />
            <Text
              fontSize="9px"
              fontWeight={700}
              color="purple.500"
              textTransform="uppercase"
              letterSpacing="0.1em"
            >
              {group.label}
            </Text>
          </Box>,
          ...group.types.map((v) => (
            <MenuItem
              key={v}
              onClick={() => onChange(v)}
              pl={4}
              py={1.5}
              fontSize="sm"
              bg={value === v ? gupySelBg : "transparent"}
              fontWeight={value === v ? 600 : 400}
              color={value === v ? "purple.600" : undefined}
              _dark={{ color: value === v ? "purple.300" : undefined }}
              _hover={{ bg: value === v ? gupySelBg : hoverBg }}
              icon={<Icon as={Sparkles} boxSize={2.5} color="purple.300" />}
            >
              {FIELD_TYPE_LABELS[v]}
            </MenuItem>
          )),
        ])}
      </MenuList>
    </Menu>
  );
}

interface FieldModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialData: FieldFormState;
  isExisting: boolean;
  onSave: (data: FieldFormState) => void;
  allFields: FormBuilderField[];
  allMissions?: import("../types").MissionDefinition[];
}

function FieldModal({ isOpen, onClose, initialData, isExisting, onSave, allFields, allMissions = [] }: FieldModalProps) {
  const [data, setData] = useState<FieldFormState>(initialData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [rulesOpen, setRulesOpen] = useState(false);
  const [availableKeys, setAvailableKeys] = useState<AvailableKey[]>([]);
  const { request: requestMissions, isLoading: loadingMissions } = useApiRequest();

  const fetchMissions = useCallback(async () => {
    if (availableKeys.length > 0) return;
    const missionsData = await requestMissions({
      url: `${settings.environment.VITE_STARTER_PACK_BASE_URL}/api/v1/admin/missions`,
      method: "GET",
      skipToast: true,
    }).catch(() => []);
    if (Array.isArray(missionsData)) {
      const keys: AvailableKey[] = [];
      const seen = new Set<string>();
      (missionsData as any[]).forEach(m => {
        (m.fields ?? []).filter((f: any) => !f.reference_source).forEach((f: any) => {
          if (!seen.has(f.key)) {
            seen.add(f.key);
            keys.push({ key: f.key, label: f.label, mission_title: m.title });
          }
        });
      });
      setAvailableKeys(keys);
    }
  }, [availableKeys.length]);

  useEffect(() => {
    if (isOpen) {
      setData(initialData);
      setErrors({});
      // Se o campo já tem referência configurada, busca as missões automaticamente
      if (initialData.reference_source) {
        fetchMissions();
      }
    }
  }, [isOpen]);

  const validate = (): boolean => {
    const errs: Record<string, string> = {};

    if (data.reference_source !== null && data.reference_source !== undefined) {
      if (!data.reference_source?.field_key?.trim()) {
        errs.reference_source = "Selecione o campo de origem";
      }
      setErrors(errs);
      return Object.keys(errs).length === 0;
    }

    if (!isExisting && !data.key.trim()) errs.key = "Chave é obrigatória";
    if (!data.label.trim()) errs.label = "Label é obrigatório";
    if (!data.is_pj && !data.is_clt) errs.regime = "Selecione ao menos um regime (PJ ou CLT)";
    if (NEEDS_OPTIONS.includes(data.field_type) && data.options.length === 0)
      errs.options = "Adicione ao menos uma opção";

    const ft = data.field_type;
    const r = (data.validation_rules ?? {}) as Record<string, unknown>;

    if (ft === "birth_date" && r.min_age !== undefined && r.max_age !== undefined) {
      if ((r.min_age as number) > (r.max_age as number))
        errs.rule_min_age = "Idade mínima deve ser menor que a máxima";
    }

    if (ft === "number" && r.min !== undefined && r.max !== undefined) {
      if ((r.min as number) > (r.max as number))
        errs.rule_min = "Valor mínimo deve ser menor que o máximo";
    }

    if (ft === "date" || ft === "birth_date") {
      const validDays = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
      const aw = r.allowed_weekdays as string[] | undefined;
      if (aw !== undefined) {
        if (!Array.isArray(aw) || aw.length === 0 || !aw.every((d) => validDays.includes(d)))
          errs.rule_allowed_weekdays = "Selecione ao menos um dia válido (Seg–Dom)";
      }
      const mft = r.max_from_today as { amount?: number; unit?: string } | undefined;
      if (mft !== undefined) {
        if (
          typeof mft.amount !== "number" || Number.isNaN(mft.amount) || mft.amount < 0
          || !["days", "months", "years"].includes(mft.unit ?? "")
        ) {
          errs.rule_max_from_today = "Informe quantidade (>=0) e unidade válida";
        }
      }
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const rules = (data.validation_rules ?? {}) as Record<string, unknown>;

  const updateRule = (key: string, value: unknown) => {
    const updated = { ...rules, [key]: value };
    (Object.keys(updated) as string[]).forEach(k => {
      if (updated[k] === undefined || updated[k] === "") delete updated[k];
    });
    setData({
      ...data,
      validation_rules: Object.keys(updated).length > 0 ? (updated as ValidationRules) : null,
    });
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave(data);
    onClose();
  };

  const addOption = () =>
    setData({ ...data, options: [...data.options, { label: "", value: "" }] });
  const updateOption = (idx: number, field: keyof FieldOption, value: string) => {
    const opts = [...data.options];
    opts[idx] = { ...opts[idx], [field]: value };
    setData({ ...data, options: opts });
  };
  const removeOption = (idx: number) =>
    setData({ ...data, options: data.options.filter((_, i) => i !== idx) });

  const bg = useColorModeValue("white", "#0f2040");
  const inputBg = useColorModeValue("gray.50", "#0a1628");
  const inputBorder = useColorModeValue("gray.200", "whiteAlpha.200");
  const optionsBg = useColorModeValue("gray.50", "#0a1628");
  const ruleBoxBg = useColorModeValue("white", "whiteAlpha.50");
  const ruleBorder = useColorModeValue("gray.100", "whiteAlpha.100");

  const isViewOnly = !!data.reference_source;

  const handleToggleMode = (viewOnly: boolean) => {
    if (viewOnly) {
      fetchMissions();
      setData({ ...data, key: "", label: "", reference_source: { field_key: "" }, options: [], validation_rules: null, visibility_rules: null });
    } else {
      setData({ ...data, reference_source: null });
    }
  };

  // Usa a mesma key do campo de origem — sem prefixo (chave única por missão no DB)
  // Só adiciona sufixo se já existe outro campo com essa key nesta mesma missão
  const generateViewKey = (sourceKey: string): string => {
    const existingKeys = new Set(
      allFields
        .filter(f => f.uid !== (initialData as any).uid)
        .map(f => f.key)
    );
    if (!existingKeys.has(sourceKey)) return sourceKey;
    let i = 2;
    while (existingKeys.has(`${sourceKey}_${i}`)) i++;
    return `${sourceKey}_${i}`;
  };

  const handleViewSourceChange = (key: string | null) => {
    if (!key) {
      setData({ ...data, reference_source: { field_key: "" }, key: "", label: "" });
      return;
    }
    const sourceField = availableKeys.find(k => k.key === key);
    const autoKey = generateViewKey(key);
    const autoLabel = sourceField?.label ?? key;
    setData({ ...data, reference_source: { field_key: key }, key: autoKey, label: autoLabel });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered scrollBehavior="inside">
      <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.400" />
      <ModalContent bg={bg} borderRadius="xl" maxH="90vh">
        <ModalHeader fontSize="md" fontWeight={600} pb={2}>
          {isExisting ? "Editar Campo" : "Novo Campo"}
        </ModalHeader>
        <ModalCloseButton top={3} />
        <ModalBody pt={0} overflowY="auto">
          <VStack spacing={4} align="stretch">

            {/* Toggle de tipo de campo */}
            <SimpleGrid columns={2} spacing={2}>
              <Button
                size="sm"
                variant={!isViewOnly ? "solid" : "outline"}
                colorScheme={!isViewOnly ? "blue" : "gray"}
                onClick={() => handleToggleMode(false)}
                borderRadius="md"
              >
                Preenchimento
              </Button>
              <Button
                size="sm"
                variant={isViewOnly ? "solid" : "outline"}
                colorScheme={isViewOnly ? "purple" : "gray"}
                onClick={() => handleToggleMode(true)}
                borderRadius="md"
              >
                Visualização (RH)
              </Button>
            </SimpleGrid>

            {isViewOnly && (
              <Box bg="purple.50" _dark={{ bg: "rgba(128,90,213,0.08)" }} borderRadius="lg" px={3} py={2.5}>
                <Text fontSize="xs" color="purple.600" _dark={{ color: "purple.300" }}>
                  Este campo <strong>não aparece para o candidato</strong>. Ele replica o valor de outro campo já preenchido, visível apenas para o RH.
                </Text>
              </Box>
            )}

            {!isViewOnly && <SimpleGrid columns={2} spacing={3}>
              <FormControl isInvalid={!!errors.key} isRequired>
                <FormLabel fontSize="xs" fontWeight={600} color="gray.500" textTransform="uppercase" letterSpacing="wide">
                  Chave
                </FormLabel>
                <Input
                  bg={inputBg}
                  fontFamily="mono"
                  fontSize="sm"
                  placeholder="ex: nome_completo"
                  value={data.key}
                  onChange={(e) => setData({ ...data, key: e.target.value })}
                  isDisabled={isExisting}
                />
                <FormErrorMessage fontSize="xs">{errors.key}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.label} isRequired>
                <FormLabel fontSize="xs" fontWeight={600} color="gray.500" textTransform="uppercase" letterSpacing="wide">
                  Nome do campo
                </FormLabel>
                <Input
                  bg={inputBg}
                  fontSize="sm"
                  placeholder="ex: Nome Completo"
                  value={data.label}
                  onChange={(e) => setData({ ...data, label: e.target.value })}
                />
                <FormErrorMessage fontSize="xs">{errors.label}</FormErrorMessage>
              </FormControl>
            </SimpleGrid>}

            {/* Campo de visualização: apenas o seletor de referência */}
            {isViewOnly ? (
              <VStack spacing={4} align="stretch">
                <FormControl isRequired isInvalid={!!errors.reference_source}>
                  <FormLabel fontSize="xs" fontWeight={600} color="gray.500" textTransform="uppercase" letterSpacing="wide">
                    Campo de origem
                  </FormLabel>
                  <FieldKeySelector
                    value={data.reference_source?.field_key ?? ""}
                    keysByMission={(() => {
                      const groups: Record<string, AvailableKey[]> = {};
                      availableKeys.forEach(k => {
                        if (!groups[k.mission_title]) groups[k.mission_title] = [];
                        groups[k.mission_title].push(k);
                      });
                      return groups;
                    })()}
                    onChange={handleViewSourceChange}
                    isLoading={loadingMissions}
                    placeholder="— selecione o campo a replicar —"
                  />
                  {errors.reference_source && (
                    <FormErrorMessage fontSize="xs">{errors.reference_source}</FormErrorMessage>
                  )}
                </FormControl>

                {data.reference_source?.field_key && (
                  <Box bg="gray.50" _dark={{ bg: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.1)" }} borderRadius="lg" p={3} borderWidth="1px" borderColor="gray.200">
                    <Text fontSize="xs" fontWeight={600} color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={2}>Gerado automaticamente</Text>
                    <HStack spacing={4}>
                      <Box>
                        <Text fontSize="10px" color="gray.400" mb={0.5}>Chave</Text>
                        <Text fontSize="sm" fontFamily="mono" fontWeight={600} color="purple.500">{data.key}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="10px" color="gray.400" mb={0.5}>Nome</Text>
                        <Text fontSize="sm" fontWeight={600}>{data.label}</Text>
                      </Box>
                    </HStack>
                  </Box>
                )}
              </VStack>
            ) : (
              <>
            <FormControl>
              <FormLabel fontSize="xs" fontWeight={600} color="gray.500" textTransform="uppercase" letterSpacing="wide">
                Detalhes
              </FormLabel>
              <RichTextEditor
                value={data.details}
                onChange={(html) => setData({ ...data, details: html })}
                placeholder="Contexto, instruções ou links úteis para o candidato preencher este campo..."
                minH="96px"
              />
            </FormControl>

            <VStack spacing={3} align="stretch">
              <FormControl>
                <FormLabel
                  fontSize="xs"
                  fontWeight={600}
                  color="gray.500"
                  textTransform="uppercase"
                  letterSpacing="wide"
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  Tipo
                  <Tooltip
                    label='Tipos "Gupy" são pré-preenchidos automaticamente com os dados do candidato importados da plataforma Gupy — o candidato não precisa digitar.'
                    placement="top"
                    hasArrow
                    fontSize="xs"
                    maxW="280px"
                    textTransform="none"
                    letterSpacing="normal"
                    fontWeight={400}
                  >
                    <Box as="span" display="inline-flex" alignItems="center" ml={0.5}>
                      <Icon as={Info} boxSize={3} color="gray.400" cursor="help" />
                    </Box>
                  </Tooltip>
                </FormLabel>
                <FieldTypeSelect
                  value={data.field_type}
                  inputBg={inputBg}
                  onChange={(v) => { setData({ ...data, field_type: v, options: [], validation_rules: v === "birth_date" ? { max_date: "today" } : null }); setRulesOpen(false); }}
                />
              </FormControl>

              <SimpleGrid columns={3} spacing={3}>
                <FormControl>
                  <FormLabel fontSize="xs" fontWeight={600} color="gray.500" textTransform="uppercase" letterSpacing="wide">
                    Obrigatório
                  </FormLabel>
                  <Flex align="center" h="40px" gap={3}>
                    <Switch
                      colorScheme="blue"
                      isChecked={data.is_required}
                      onChange={(e) => setData({ ...data, is_required: e.target.checked })}
                    />
                    <Text fontSize="sm" color="gray.500">
                      {data.is_required ? "Sim" : "Não"}
                    </Text>
                  </Flex>
                </FormControl>

                <FormControl isInvalid={!!errors.regime}>
                  <FormLabel fontSize="xs" fontWeight={600} color="gray.500" textTransform="uppercase" letterSpacing="wide">
                    Exibir para PJ
                  </FormLabel>
                  <Flex align="center" h="40px" gap={3}>
                    <Switch
                      colorScheme="orange"
                      isChecked={data.is_pj}
                      onChange={(e) => setData({ ...data, is_pj: e.target.checked })}
                    />
                    <Text fontSize="sm" color="gray.500">
                      {data.is_pj ? "Sim" : "Não"}
                    </Text>
                  </Flex>
                </FormControl>

                <FormControl isInvalid={!!errors.regime}>
                  <FormLabel fontSize="xs" fontWeight={600} color="gray.500" textTransform="uppercase" letterSpacing="wide">
                    Exibir para CLT
                  </FormLabel>
                  <Flex align="center" h="40px" gap={3}>
                    <Switch
                      colorScheme="teal"
                      isChecked={data.is_clt}
                      onChange={(e) => setData({ ...data, is_clt: e.target.checked })}
                    />
                    <Text fontSize="sm" color="gray.500">
                      {data.is_clt ? "Sim" : "Não"}
                    </Text>
                  </Flex>
                  {errors.regime && (
                    <FormErrorMessage fontSize="xs">{errors.regime}</FormErrorMessage>
                  )}
                </FormControl>
              </SimpleGrid>
            </VStack>

            {/* Banner contextual para tipos Gupy */}
            {GUPY_FIELD_TYPES.includes(data.field_type) && (
              <Box
                p={3}
                borderRadius="lg"
                bg="purple.50"
                borderWidth="1px"
                borderColor="purple.200"
                _dark={{ bg: "rgba(128,90,213,0.08)", borderColor: "rgba(128,90,213,0.25)" }}
              >
                <HStack spacing={2} align="flex-start">
                  <Flex
                    w="28px"
                    h="28px"
                    borderRadius="md"
                    bg="purple.100"
                    align="center"
                    justify="center"
                    flexShrink={0}
                    _dark={{ bg: "rgba(128,90,213,0.2)" }}
                  >
                    <Icon as={Sparkles} boxSize={3.5} color="purple.500" />
                  </Flex>
                  <Box>
                    <Text fontSize="xs" fontWeight={700} color="purple.700" _dark={{ color: "purple.300" }} mb={0.5}>
                      Dado do candidato — Gupy
                    </Text>
                    <Text fontSize="xs" color="purple.600" _dark={{ color: "purple.400" }} lineHeight={1.5}>
                      Este campo será pré-preenchido automaticamente com o <strong>{FIELD_TYPE_LABELS[data.field_type]}</strong> do candidato cadastrado na plataforma Gupy. O candidato poderá editar se necessário.
                    </Text>
                  </Box>
                </HStack>
              </Box>
            )}

            {NEEDS_OPTIONS.includes(data.field_type) && (
              <Box>
                <Flex justify="space-between" align="center" mb={2}>
                  <Text fontSize="xs" fontWeight={600} color="gray.500" textTransform="uppercase" letterSpacing="wide">
                    Opções
                  </Text>
                  <Button
                    size="xs"
                    variant="ghost"
                    colorScheme="blue"
                    leftIcon={<Plus size={12} />}
                    onClick={addOption}
                  >
                    Adicionar
                  </Button>
                </Flex>
                {errors.options && (
                  <Text color="red.500" fontSize="xs" mb={2}>{errors.options}</Text>
                )}
                <VStack spacing={2} p={3} bg={optionsBg} borderRadius="lg">
                  {data.options.length === 0 ? (
                    <Text fontSize="xs" color="gray.400" py={2}>Nenhuma opção ainda.</Text>
                  ) : (
                    data.options.map((opt, oIdx) => (
                      <HStack key={oIdx} w="full" spacing={2}>
                        <Input
                          size="sm"
                          fontFamily="mono"
                          placeholder="valor"
                          value={opt.value}
                          onChange={(e) => updateOption(oIdx, "value", e.target.value)}
                        />
                        <Input
                          size="sm"
                          placeholder="texto visível"
                          value={opt.label}
                          onChange={(e) => updateOption(oIdx, "label", e.target.value)}
                        />
                        <IconButton
                          aria-label="Remover"
                          icon={<X size={13} />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          flexShrink={0}
                          onClick={() => removeOption(oIdx)}
                        />
                      </HStack>
                    ))
                  )}
                </VStack>
              </Box>
            )}

            {/* Validation rules — date fields */}
            {(data.field_type === "date" || data.field_type === "birth_date") && (
              <Box>
                <Divider mb={3} />
                {!rulesOpen ? (
                  <Button
                    variant="ghost"
                    colorScheme="blue"
                    size="sm"
                    w="full"
                    border="1.5px dashed"
                    borderColor="blue.400"
                    color="blue.500"
                    _dark={{ borderColor: "blue.300", color: "blue.300" }}
                    leftIcon={<Icon as={Plus} boxSize={3.5} />}
                    onClick={() => setRulesOpen(true)}
                  >
                    Adicionar regras de validação
                  </Button>
                ) : (
                  <HStack justify="space-between" mb={3}>
                    <Text fontSize="xs" fontWeight={600} color="gray.500" textTransform="uppercase" letterSpacing="wide">
                      Regras de validação
                    </Text>
                    <Button
                      variant="ghost"
                      size="xs"
                      color="red.400"
                      onClick={() => { setRulesOpen(false); setData({ ...data, validation_rules: data.field_type === "birth_date" ? { max_date: "today" } : null }); }}
                    >
                      Remover regras
                    </Button>
                  </HStack>
                )}
                <Collapse in={rulesOpen} animateOpacity>
                <VStack spacing={3} p={3} bg={optionsBg} borderRadius="lg">
                  {data.field_type === "birth_date" && (
                    <SimpleGrid columns={2} spacing={3} w="full">
                      <FormControl isInvalid={!!errors.rule_min_age}>
                        <FormLabel fontSize="xs" fontWeight={500} color="gray.500">Idade mínima</FormLabel>
                        <NumberInput
                          min={0}
                          max={150}
                          value={(rules.min_age as number) ?? ""}
                          onChange={(v) => updateRule("min_age", v === "" ? undefined : Number(v))}
                        >
                          <NumberInputField placeholder="ex: 18" fontSize="sm" />
                        </NumberInput>
                        <FormErrorMessage fontSize="xs">{errors.rule_min_age}</FormErrorMessage>
                      </FormControl>

                      <FormControl>
                        <FormLabel fontSize="xs" fontWeight={500} color="gray.500">Idade máxima</FormLabel>
                        <NumberInput
                          min={0}
                          max={150}
                          value={(rules.max_age as number) ?? ""}
                          onChange={(v) => updateRule("max_age", v === "" ? undefined : Number(v))}
                        >
                          <NumberInputField placeholder="ex: 65" fontSize="sm" />
                        </NumberInput>
                      </FormControl>
                    </SimpleGrid>
                  )}

                  {data.field_type === "date" && (() => {
                    const DAY_OPTS: { code: string; label: string; full: string }[] = [
                      { code: "mon", label: "Seg", full: "Segunda" },
                      { code: "tue", label: "Ter", full: "Terça" },
                      { code: "wed", label: "Qua", full: "Quarta" },
                      { code: "thu", label: "Qui", full: "Quinta" },
                      { code: "fri", label: "Sex", full: "Sexta" },
                      { code: "sat", label: "Sáb", full: "Sábado" },
                      { code: "sun", label: "Dom", full: "Domingo" },
                    ];
                    const aw = (rules.allowed_weekdays as string[] | undefined) ?? [];
                    const toggleDay = (code: string) => {
                      const next = aw.includes(code) ? aw.filter((d) => d !== code) : [...aw, code];
                      updateRule("allowed_weekdays", next.length > 0 ? next : undefined);
                    };
                    const mft = (rules.max_from_today as { amount?: number; unit?: string } | undefined) ?? {};
                    const setMaxFromToday = (patch: Partial<{ amount: number; unit: string }>) => {
                      const next = { amount: mft.amount, unit: mft.unit ?? "days", ...patch };
                      const hasAmount = typeof next.amount === "number" && !Number.isNaN(next.amount);
                      const meaningful = hasAmount || next.unit !== "days" || !!rules.max_from_today;
                      updateRule("max_from_today", meaningful ? next : undefined);
                    };
                    return (
                      <VStack spacing={3} align="stretch" w="full">
                        {/* Janela máxima */}
                        <Box p={3} borderRadius="md" borderWidth="1px" borderColor={ruleBorder} bg={ruleBoxBg}>
                          <FormControl isInvalid={!!errors.rule_max_from_today}>
                            <FormLabel fontSize="sm" fontWeight={600} color="gray.600" _dark={{ color: "gray.300" }} mb={2}>
                              Janela máxima a partir de hoje
                            </FormLabel>
                            <Text fontSize="xs" color="gray.500" mb={2}>A data deve estar entre hoje e hoje + N (dias/meses/anos)</Text>
                            <HStack spacing={2}>
                              <Input
                                type="number"
                                min={0}
                                size="sm"
                                w="110px"
                                placeholder="ex: 5"
                                value={mft.amount ?? ""}
                                onChange={(e) => setMaxFromToday({ amount: e.target.value === "" ? undefined : Number(e.target.value) })}
                                bg={inputBg}
                                borderColor={inputBorder}
                              />
                              <Select
                                size="sm"
                                w="130px"
                                value={mft.unit ?? "days"}
                                onChange={(e) => setMaxFromToday({ unit: e.target.value })}
                                bg={inputBg}
                                borderColor={inputBorder}
                              >
                                <option value="days">Dias</option>
                                <option value="months">Meses</option>
                                <option value="years">Anos</option>
                              </Select>
                            </HStack>
                            <FormErrorMessage fontSize="xs">{errors.rule_max_from_today}</FormErrorMessage>
                          </FormControl>
                        </Box>

                        {/* Dias da semana */}
                        <Box p={3} borderRadius="md" borderWidth="1px" borderColor={ruleBorder} bg={ruleBoxBg}>
                          <FormControl isInvalid={!!errors.rule_allowed_weekdays}>
                            <FormLabel fontSize="sm" fontWeight={600} color="gray.600" _dark={{ color: "gray.300" }} mb={2}>
                              Dias da semana permitidos
                            </FormLabel>
                            <Text fontSize="xs" color="gray.500" mb={2}>Selecione os dias em que o agendamento é permitido</Text>
                            <HStack spacing={2} wrap="wrap">
                              {DAY_OPTS.map((d) => {
                                const checked = aw.includes(d.code);
                                return (
                                  <Box
                                    as="button"
                                    type="button"
                                    key={d.code}
                                    onClick={() => toggleDay(d.code)}
                                    px={3}
                                    py={1.5}
                                    borderRadius="full"
                                    borderWidth="1.5px"
                                    borderColor={checked ? "blue.400" : ruleBorder}
                                    bg={checked ? "blue.50" : "transparent"}
                                    color={checked ? "blue.600" : "gray.600"}
                                    _dark={{
                                      bg: checked ? "rgba(0,158,251,0.12)" : "transparent",
                                      borderColor: checked ? "blue.300" : ruleBorder,
                                      color: checked ? "blue.300" : "gray.300",
                                    }}
                                    fontWeight={checked ? 600 : 500}
                                    fontSize="sm"
                                    cursor="pointer"
                                    _hover={{ borderColor: "blue.300" }}
                                    title={d.full}
                                  >
                                    {d.label}
                                  </Box>
                                );
                              })}
                            </HStack>
                            <FormErrorMessage fontSize="xs">{errors.rule_allowed_weekdays}</FormErrorMessage>
                          </FormControl>
                        </Box>
                      </VStack>
                    );
                  })()}
                </VStack>
                </Collapse>
              </Box>
            )}

            {/* Validation rules — number fields */}
            {data.field_type === "number" && (
              <Box>
                <Divider mb={3} />
                {!rulesOpen ? (
                  <Button
                    variant="ghost"
                    colorScheme="blue"
                    size="sm"
                    w="full"
                    border="1.5px dashed"
                    borderColor="blue.400"
                    color="blue.500"
                    _dark={{ borderColor: "blue.300", color: "blue.300" }}
                    leftIcon={<Icon as={Plus} boxSize={3.5} />}
                    onClick={() => setRulesOpen(true)}
                  >
                    Adicionar regras de validação
                  </Button>
                ) : (
                  <HStack justify="space-between" mb={3}>
                    <Text fontSize="xs" fontWeight={600} color="gray.500" textTransform="uppercase" letterSpacing="wide">
                      Regras de validação
                    </Text>
                    <Button
                      variant="ghost"
                      size="xs"
                      color="red.400"
                      onClick={() => { setRulesOpen(false); setData({ ...data, validation_rules: null }); }}
                    >
                      Remover regras
                    </Button>
                  </HStack>
                )}
                <Collapse in={rulesOpen} animateOpacity>
                <VStack spacing={3} p={3} bg={optionsBg} borderRadius="lg">
                  <SimpleGrid columns={2} spacing={3} w="full">
                    <FormControl isInvalid={!!errors.rule_min}>
                      <FormLabel fontSize="xs" fontWeight={500} color="gray.500">Valor mínimo</FormLabel>
                      <NumberInput
                        value={(rules.min as number) ?? ""}
                        onChange={(v) => updateRule("min", v === "" ? undefined : Number(v))}
                      >
                        <NumberInputField placeholder="ex: 0" fontSize="sm" />
                      </NumberInput>
                      <FormErrorMessage fontSize="xs">{errors.rule_min}</FormErrorMessage>
                    </FormControl>

                    <FormControl>
                      <FormLabel fontSize="xs" fontWeight={500} color="gray.500">Valor máximo</FormLabel>
                      <NumberInput
                        value={(rules.max as number) ?? ""}
                        onChange={(v) => updateRule("max", v === "" ? undefined : Number(v))}
                      >
                        <NumberInputField placeholder="ex: 100" fontSize="sm" />
                      </NumberInput>
                    </FormControl>
                  </SimpleGrid>

                  <FormControl>
                    <Flex align="center" h="32px" gap={3}>
                      <Switch
                        colorScheme="blue"
                        size="sm"
                        isChecked={(rules.integer_only as boolean) ?? false}
                        onChange={(e) => updateRule("integer_only", e.target.checked || undefined)}
                      />
                      <Text fontSize="sm" color="gray.500">Somente inteiros</Text>
                    </Flex>
                  </FormControl>
                </VStack>
                </Collapse>
              </Box>
            )}

            {/* Visibility rules */}
            <Box>
              <Divider mb={3} />
              <HStack justify="space-between" mb={3}>
                <Text fontSize="xs" fontWeight={600} color="gray.500" textTransform="uppercase" letterSpacing="wide">
                  Regras de visibilidade
                </Text>
              </HStack>
              <VisibilityRulesBuilder
                rules={data.visibility_rules}
                allFields={allFields}
                currentFieldKey={data.key}
                onChange={(rules) => setData({ ...data, visibility_rules: rules })}
              />
            </Box>

              </>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter gap={2} pt={4}>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button colorScheme="blue" size="sm" onClick={handleSave}>
            {isExisting ? "Salvar" : "Adicionar Campo"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

interface FormBuilderProps {
  fields: FormBuilderField[];
  onReorder: (startIndex: number, endIndex: number) => void;
  onAdd: (field: Omit<FormBuilderField, "uid">, insertAfterKey?: string) => void;
  onUpdate: (index: number, field: Omit<FormBuilderField, "uid">, insertAfterKey?: string) => void;
  onDelete: (index: number) => void;
  pendingQuickAdd?: FieldType | null;
  onClearPendingQuickAdd?: () => void;
  hideAddButton?: boolean;
  allMissions?: import("../types").MissionDefinition[];
}

export function FormBuilder({ fields, onReorder, onAdd, onUpdate, onDelete, pendingQuickAdd, onClearPendingQuickAdd, hideAddButton, allMissions = [] }: FormBuilderProps) {
  const [modal, setModal] = useState<{
    isOpen: boolean;
    mode: "add" | "edit";
    index: number | null;
    data: FieldFormState;
  }>({ isOpen: false, mode: "add", index: null, data: DEFAULT_FIELD });

  const rowBg = useColorModeValue("white", "#0f2040");
  const rowBorder = useColorModeValue("gray.100", "whiteAlpha.80");
  const gripColor = useColorModeValue("gray.300", "gray.600");
  const addBtnBorder = useColorModeValue("gray.200", "whiteAlpha.100");
  const addBtnHoverBg = useColorModeValue("blue.50", "rgba(0,158,251,0.06)");

  const openAdd = () =>
    setModal({ isOpen: true, mode: "add", index: null, data: DEFAULT_FIELD });

  const openEdit = (index: number) =>
    setModal({
      isOpen: true,
      mode: "edit",
      index,
      data: {
        key: fields[index].key,
        label: fields[index].label,
        details: fields[index].details || "",
        field_type: fields[index].field_type,
        is_required: fields[index].is_required,
        is_pj: fields[index].is_pj,
        is_clt: fields[index].is_clt,
        options: fields[index].options,
        validation_rules: fields[index].field_type === "birth_date"
          ? { max_date: "today", ...(fields[index].validation_rules ?? {}) }
          : fields[index].validation_rules,
        visibility_rules: fields[index].visibility_rules ?? null,
        reference_source: fields[index].reference_source ?? null,
      },
    });

  useEffect(() => {
    if (pendingQuickAdd) {
      setModal({ isOpen: true, mode: "add", index: null, data: { ...DEFAULT_FIELD, field_type: pendingQuickAdd } });
      onClearPendingQuickAdd?.();
    }
  }, [pendingQuickAdd]);

  const handleSave = (data: FieldFormState) => {
    const dependsOnKey = data.visibility_rules?.conditions?.[0]?.field_key;
    if (modal.mode === "add") {
      onAdd(data, dependsOnKey);
    } else if (modal.index !== null) {
      onUpdate(modal.index, data, dependsOnKey);
    }
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    onReorder(result.source.index, result.destination.index);
  };

  return (
    <Box>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="builder-fields">
          {(provided) => (
            <Box {...provided.droppableProps} ref={provided.innerRef}>
              {fields.length === 0 && (
                <Flex
                  align="center"
                  justify="center"
                  py={6}
                  mb={2}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderStyle="dashed"
                  borderColor={addBtnBorder}
                >
                  <Text fontSize="sm" color="gray.400">
                    Nenhum campo criado ainda.
                  </Text>
                </Flex>
              )}

              {fields.map((field, index) => (
                <Draggable
                  key={field.uid}
                  draggableId={String(field.uid)}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <Flex
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      align="center"
                      gap={3}
                      px={3}
                      py={2.5}
                      mb={1.5}
                      bg={rowBg}
                      borderRadius="lg"
                      borderWidth="1px"
                      borderColor={rowBorder}
                      boxShadow={snapshot.isDragging ? "md" : "none"}
                      transition="box-shadow 0.15s"
                    >
                      <Box
                        {...provided.dragHandleProps}
                        color={gripColor}
                        cursor="grab"
                        _active={{ cursor: "grabbing" }}
                        flexShrink={0}
                      >
                        <GripVertical size={16} />
                      </Box>

                      <Text
                        fontSize="xs"
                        color="gray.400"
                        w="18px"
                        textAlign="right"
                        flexShrink={0}
                      >
                        {index + 1}
                      </Text>

                      <Box flex={1} minW={0}>
                        <Text fontSize="sm" fontWeight={500} noOfLines={1}>
                          {field.label}
                        </Text>
                        <Text
                          fontSize="xs"
                          color="gray.500"
                          fontFamily="mono"
                          noOfLines={1}
                        >
                          {field.key}
                        </Text>
                      </Box>

                      <HStack spacing={1.5} flexShrink={0}>
                        {GUPY_FIELD_TYPES.includes(field.field_type) ? (
                          <Tooltip
                            label={`Pré-preenchido com "${FIELD_TYPE_LABELS[field.field_type]}" importado da Gupy — o candidato não precisa digitar.`}
                            placement="top"
                            hasArrow
                            fontSize="xs"
                            maxW="260px"
                          >
                            <Badge
                              colorScheme="purple"
                              variant="subtle"
                              fontSize="xs"
                              fontWeight={500}
                              cursor="help"
                              display="inline-flex"
                              alignItems="center"
                              gap={1}
                            >
                              <Icon as={Sparkles} boxSize={2.5} />
                              {FIELD_TYPE_LABELS[field.field_type]}
                            </Badge>
                          </Tooltip>
                        ) : (
                          <Badge colorScheme="gray" variant="subtle" fontSize="xs" fontWeight={500}>
                            {FIELD_TYPE_LABELS[field.field_type]}
                          </Badge>
                        )}
                        {GUPY_FIELD_TYPES.includes(field.field_type) && (
                          <Tooltip
                            label="Dados importados automaticamente da plataforma Gupy"
                            placement="top"
                            hasArrow
                            fontSize="xs"
                          >
                            <Badge colorScheme="purple" variant="outline" fontSize="xs" fontWeight={600} cursor="help">
                              Gupy
                            </Badge>
                          </Tooltip>
                        )}
                        {field.is_required && (
                          <Badge colorScheme="orange" variant="subtle" fontSize="xs">
                            Obrig.
                          </Badge>
                        )}
                        {field.is_pj && (
                          <Badge colorScheme="yellow" variant="subtle" fontSize="xs">
                            PJ
                          </Badge>
                        )}
                        {field.is_clt && (
                          <Badge colorScheme="teal" variant="subtle" fontSize="xs">
                            CLT
                          </Badge>
                        )}
                        {field.details && (
                          <Badge colorScheme="teal" variant="subtle" fontSize="xs">
                            Detalhes
                          </Badge>
                        )}
                        {field.validation_rules && Object.keys(field.validation_rules).length > 0 && (
                          <Badge colorScheme="cyan" variant="subtle" fontSize="xs">
                            Regras
                          </Badge>
                        )}
                        {field.reference_source !== null && field.reference_source !== undefined && (
                          <Tooltip label={field.reference_source?.field_key ? `Visualização RH — replica "${field.reference_source.field_key}". Não aparece para o candidato.` : "Visualização RH — campo de origem não selecionado."} hasArrow placement="top" fontSize="xs" maxW="280px">
                            <Badge
                              bg="rgba(128,90,213,0.15)"
                              color="purple.500"
                              _dark={{ bg: "rgba(128,90,213,0.2)", color: "purple.300" }}
                              border="1px solid"
                              borderColor="purple.200"
                              fontSize="xs"
                              fontWeight={700}
                              display="inline-flex"
                              alignItems="center"
                              gap="4px"
                              px={1.5}
                              cursor="help"
                            >
                              <Icon as={Link2} boxSize={2.5} />
                              Vis. RH
                            </Badge>
                          </Tooltip>
                        )}
                      </HStack>

                      <HStack spacing={0} flexShrink={0}>
                        <IconButton
                          aria-label="Editar campo"
                          icon={<Pencil size={14} />}
                          size="xs"
                          variant="ghost"
                          onClick={() => openEdit(index)}
                        />
                        <IconButton
                          aria-label="Remover campo"
                          icon={<Trash2 size={14} />}
                          size="xs"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => onDelete(index)}
                        />
                      </HStack>
                    </Flex>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>

      {!hideAddButton && (
        <Button
          mt={1.5}
          w="full"
          size="sm"
          variant="ghost"
          leftIcon={<Plus size={14} />}
          onClick={openAdd}
          color="gray.500"
          borderWidth="1px"
          borderStyle="dashed"
          borderColor={addBtnBorder}
          _hover={{ color: "blue.500", borderColor: "blue.300", bg: addBtnHoverBg }}
        >
          Adicionar Campo
        </Button>
      )}

      <FieldModal
        isOpen={modal.isOpen}
        onClose={() => setModal((s) => ({ ...s, isOpen: false }))}
        initialData={modal.data}
        isExisting={modal.mode === "edit"}
        onSave={handleSave}
        allFields={fields}
        allMissions={allMissions}
      />
    </Box>
  );
}
